/**
 * autoPrint — shared helper used by both NotificationCenter (background
 * auto-print on any admin page) and AdminOrders (when admin is on the
 * orders page itself). Idempotent: a single order id is never printed
 * twice in the same browser session.
 *
 * Hardware-agnostic: routes through /app/frontend/src/services/printer/
 * so the same auto-print flow works for network thermal printers AND
 * Android POS built-in printers without any conditional here.
 */
import axios from 'axios';
import { enqueue as enqueuePrint, dequeue as dequeuePrint } from './printQueue';
import { playPrintFailAlert } from './soundAlert';
import printerService from '../services/printer/printerService';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const printedOrderIds = new Set();

/**
 * Returns true if this order has already been printed locally.
 */
export const hasPrintedLocally = (orderId) => printedOrderIds.has(orderId);

/**
 * Mark an order as printed locally (also tells the backend so other
 * admin tabs/devices don't re-print it).
 */
export const markPrintedLocally = (orderId) => printedOrderIds.add(orderId);

/**
 * Auto-print a single order receipt if:
 *   - Auto-print is enabled in admin settings
 *   - A printer is configured for the active printer mode
 *   - Order has not been printed yet (locally or per print_status)
 *   - Order is PAID (card) or COD_PENDING (cash)
 *
 * Safely returns without printing if the active adapter is unavailable
 * (e.g. QZ Tray not running / Android bridge not injected).
 * Returns true on successful print, false otherwise.
 */
export const autoPrintOrder = async (order, settings) => {
  if (!order || !settings) return false;
  if (!settings.auto_print) return false;

  // Printer is hard-wired to the Android built-in POS printer
  // (see printerService.FORCED_MODE). No printer_name check needed.

  if (printedOrderIds.has(order.id)) return false;
  if (order.print_status && order.print_status !== 'NOT_PRINTED') return false;
  if (order.payment_status !== 'PAID' && order.payment_status !== 'COD_PENDING') return false;

  // Mark optimistically so concurrent pollers don't double-print.
  printedOrderIds.add(order.id);

  // printerService is hard-wired to Android built-in mode — always routes
  // to androidPosPrinterAdapter regardless of arguments passed here.
  const result = await printerService.printOrder(order);

  if (result.success) {
    // If this order was previously queued for retry, remove it — we're done.
    dequeuePrint(order.id);

    // Tell the backend so other devices don't reprint.
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(
        `${API_URL}/api/admin/orders/${order.id}/qz-print`,
        { success: true },
        { headers: { Authorization: `Bearer ${token}` } },
      ).catch(() => {});
    }
    return true;
  }

  // FAILURE path -----------------------------------------------------------
  // Roll back local mark so a future retry can succeed.
  printedOrderIds.delete(order.id);
  const errMsg = (result.error || 'Print failed').slice(0, 200);
  // eslint-disable-next-line no-console
  console.error('Auto-print failed:', errMsg);

  // Enqueue for background retry — the queue survives page refreshes
  // and retries every 5s.
  enqueuePrint(order, errMsg);

  // Audible "print failed" buzz on the POS device — attention-grabbing
  // over the kitchen noise. Only fires on the initial autoprint failure;
  // background retries (printQueue.retryOne) intentionally stay silent
  // to avoid a constantly-buzzing device when the printer is down.
  playPrintFailAlert();

  // Tell the backend so the admin sees a FAILED status badge.
  const token = localStorage.getItem('token');
  if (token) {
    axios.post(
      `${API_URL}/api/admin/orders/${order.id}/qz-print`,
      { success: false, error: errMsg },
      { headers: { Authorization: `Bearer ${token}` } },
    ).catch(() => {});
  }
  return false;
};
